import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Async
import PaymentsAsync from 'store/payments/paymentsAsync';
// Actions
import { reservationsActions } from 'store/reservations/reservationsSlice';
import { UiActions } from 'store/ui/uiSlice';
// Hooks
import { useAppDispatch } from 'hooks/redux';
// Mui
import {
  Button, DialogActions, DialogContent,
  TextField, Grid, ButtonGroup, Box, Checkbox, Typography
} from '@mui/material';
import {
  LoadingButton
} from '@mui/lab';
// Utilities
import { isFieldRequired } from 'utilities/Validation';

interface IFormData {
  accountNumber: string;
  routingNumber: string;
  checkHolderName: string;
  checkName: string;
  accountType: 'CHECKING' | 'SAVINGS';
  checkType: 'PERSONAL' | 'BUSINESS';
  streetAddress1: string;
  city: string;
  state: string;
  postalCode: string;
}

type Props = {
  reservationId: number,
  onClose: () => void,
}

const PaymentCheckForm:React.FC<Props> = ({ reservationId, onClose }) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<boolean>(false);

  const { control, handleSubmit, formState:{ errors } } = useForm<IFormData>({
    defaultValues: {
      accountNumber: '',
      routingNumber: '',
      checkHolderName: '',
      checkName: '',
      accountType: 'CHECKING',
      checkType: 'PERSONAL',
      streetAddress1: '',
      city: '',
      state: '',
      postalCode: '',
    }
  });

  const onSubmit = handleSubmit((data:IFormData) => {
    setIsLoading(true);
    const reservationPaymentData = {
      reservationId,
      heartlandCheckPaymentData: {...data},
    }
    dispatch(PaymentsAsync.createPayment({reservationPaymentData}))
      .unwrap()
      .then(() => {
        dispatch(reservationsActions.markAsPaidReservation(reservationId));
        dispatch(UiActions.enqueueSnackbar({ message: 'Reservation was successfully paid' }));
      })
      .catch(() => setIsLoading(false))
  });

  return (
    <React.Fragment>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {/* accountNumber */}
              <Controller
                control={control} name="accountNumber"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Account number"
                    type="text"
                    error={Boolean(errors.accountNumber)}
                    helperText={errors.accountNumber ? errors.accountNumber.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* routingNumber */}
              <Controller
                control={control} name="routingNumber"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Routing number"
                    type="text"
                    error={Boolean(errors.routingNumber)}
                    helperText={errors.routingNumber ? errors.routingNumber.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* checkHolderName */}
              <Controller
                control={control} name="checkHolderName"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Check holder name"
                    type="text"
                    error={Boolean(errors.checkHolderName)}
                    helperText={errors.checkHolderName ? errors.checkHolderName.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* checkName */}
              <Controller
                control={control} name="checkName"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Check name"
                    type="text"
                    error={Boolean(errors.checkName)}
                    helperText={errors.checkName ? errors.checkName.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {/* accountType */}
              <Controller
                control={control} name="accountType"
                render={({ field: { value, onChange } }) => (
                  <ButtonGroup aria-label="accountType button group" fullWidth>
                    <Button variant={value === 'CHECKING' ? 'contained' : 'outlined'} onClick={() => onChange('CHECKING')}>Checking</Button>
                    <Button variant={value === 'SAVINGS' ? 'contained' : 'outlined'} onClick={() => onChange('SAVINGS')}>Savings</Button>
                  </ButtonGroup>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {/* checkType */}
              <Controller
                control={control} name="checkType"
                render={({ field: { value, onChange } }) => (
                  <ButtonGroup aria-label="checkType button group" fullWidth>
                    <Button variant={value === 'PERSONAL' ? 'contained' : 'outlined'} onClick={() => onChange('PERSONAL')}>Personal</Button>
                    <Button variant={value === 'BUSINESS' ? 'contained' : 'outlined'} onClick={() => onChange('BUSINESS')}>Business</Button>
                  </ButtonGroup>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* streetAddress1 */}
              <Controller
                control={control} name="streetAddress1"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Address"
                    type="text"
                    error={Boolean(errors.streetAddress1)}
                    helperText={errors.streetAddress1 ? errors.streetAddress1.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* city */}
              <Controller
                control={control} name="city"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="City"
                    type="text"
                    error={Boolean(errors.city)}
                    helperText={errors.city ? errors.city.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* state */}
              <Controller
                control={control} name="state"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="State"
                    type="text"
                    error={Boolean(errors.state)}
                    helperText={errors.state ? errors.state.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* postalCode */}
              <Controller
                control={control} name="postalCode"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    fullWidth
                    margin='normal'
                    label="Postal code"
                    type="text"
                    error={Boolean(errors.postalCode)}
                    helperText={errors.postalCode ? errors.postalCode.message : ''}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <Box sx={{ height: '200px', overflowY: 'scroll', m: 2 }}>
        <Typography variant="h5">
          PAYMENT TERMS AND CONDITIONS
        </Typography>
        <Typography variant="h6">
          AUTHORIZATION
        </Typography>
        <Typography>
          By checking the "I accept the Terms and Conditions" checkbox below I authorize my bank to debit my specified account for the amount of my payment. This is a one-time payment which will occur on the next business day or as soon as practical thereafter. If my payment cannot be completed for any reason, including insufficient funds or error in the information which I submitted, I will retain the same liability, which is my sole responsibility, for payment as though I had not attempted to make the payment. I also understand that additional fees and penalties may be collected to the extent of applicable law.This transaction may not be disputed and is non-refundable. Dates cannot be altered.
        </Typography>
        <Typography variant="h6">
          RECEIPT
        </Typography>
        <Typography>
          A receipt can be printed after payment is accepted which may serve as evidence of payment. If an email address is provided during the payment process, a receipt will be emailed to you after the payment is processed.
        </Typography>
        <Typography variant="h6">
          CONTACT
        </Typography>
        <Typography>
          If for any reason you wish to make a change to the payment after submission, please contact Dash Realty Group as the first option for assistance at 407-913-3274. DashWeek is a brand of Dash Realty Group. It is possible that you may receive correspondence from Dash Realty Group representative. I agree to the terms and conditions.
        </Typography>
      </Box>
      <DialogActions>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox  checked={accepted} onChange={(_:any, checked: boolean) => setAccepted(checked)} />
          <Typography sx={{ fontSize: '14px' }}>
              I ACCEPT THE TERMS AND CONDITIONS
          </Typography>
        </label>
        <Box flexGrow="1" />
        <Box sx={{ minWidth: 'max-content' }}>
          <Button
            variant="text"
            color="primary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isLoading}
            onClick={onSubmit}
            variant="contained"
            color="primary"
            disabled={!accepted}
          >
            Pay
          </LoadingButton>
        </Box>
      </DialogActions>
    </React.Fragment>
  )
}

export default PaymentCheckForm;
